import { animator } from "../anim";
import { touch } from "../canvas";
import { onResize } from "../resize";
import { getDarkener } from "../utils";
import { DonutChartParams, DonutSlice, DonutUpdateData } from "./interfaces";

const HOVER_OFFSET = 5;
const TOOLTIP_OFFSET = 16;
const FADED_OPACITY = 0.3;

export const createDonutChart = ({
    canvas,
    data: initialData,
    gradient,
    onHover,
    onClick,
}: DonutChartParams) => {
    const painter = touch(canvas);
    const currentSlices: DonutSlice[] = [];
    const offsets = {
        get left() {
            return 20 * painter.dpr;
        },
        get right() {
            return 20 * painter.dpr;
        },
        get top() {
            return 20 * painter.dpr;
        },
        get bottom() {
            return 20 * painter.dpr;
        },
    };
    const currentParams = {
        colors: initialData.colors,
        labels: initialData.labels,
        values: initialData.values,
        radius: animator.anim(0),
        width: animator.anim(0),
        cx: animator.anim(0),
        cy: animator.anim(0),
        hovered: -1,
        clicked: -1,
        leaveTmId: 0,
    };
    const getDarkColor = getDarkener(0.4);

    const calcRadius = () =>
        Math.min(
            (canvas.width - offsets.left - offsets.right) / 2,
            (canvas.height - offsets.top - offsets.bottom) / 2,
        );

    const removeSlices = (start: number) => {
        const slicesToDelete = currentSlices.slice(start);

        slicesToDelete.forEach((slice) => {
            slice.end.play(slice.start.value);
            slice.opacity.play(0);
        });

        if (slicesToDelete.length > 0) {
            slicesToDelete[0].opacity.onEnd = () => {
                currentSlices.splice(start);
                const animations = slicesToDelete
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    .map(({ color: _, ...sliceAnimations }) =>
                        Object.values(sliceAnimations),
                    )
                    .flat();
                animator.clean(animations);
            };
        }
    };

    // it is a way to show the donut chart with static total value
    // in this case, each value is considered to be able to occupy the whole donut
    //
    const updateDataStatic = (data: DonutUpdateData) => {
        const total = data.total ?? 0;
        const startAngle = -Math.PI / 2;
        const length = Math.PI * 2;

        for (let i = 0; i < data.values.length; ++i) {
            const value = Math.min(data.values[i], total);
            const angle = total ? (value / total) * length : 0;
            const start = startAngle;
            const end = startAngle + angle;

            if (i < currentSlices.length) {
                currentSlices[i].start.play(start);
                currentSlices[i].end.play(end);
                currentSlices[i].color =
                    data.colors?.[i] ?? currentParams.colors[i];
            } else {
                currentSlices.push({
                    start: animator.anim(start),
                    end: animator.anim(start).play(end),
                    opacity: animator.anim(0).play(1),
                    offset: animator.anim(0),
                    color:
                        data.colors?.[i] ?? currentParams.colors[i] ?? "#000",
                });
            }
        }

        if (currentSlices.length > data.values.length) {
            removeSlices(data.values.length);
        }

        animator.forceUpdate();
    };

    // it is a classic way to show the donut chart
    // the total is a sum of all values
    //
    const updateDataClassic = (data: DonutUpdateData) => {
        const gap = Math.PI / 160;
        const finalLength =
            Math.PI * 2 -
            gap * (data.values.length === 1 ? 0 : data.values.length);
        const total = data.values.reduce((a, b) => a + b, 0);
        const startAngle = -Math.PI / 2;
        let currentAngle = startAngle;

        for (let i = 0; i < data.values.length; ++i) {
            const value = data.values[i];
            const angle = total ? (value / total) * finalLength : 0;
            const start = currentAngle;
            const end = currentAngle + angle;

            if (i < currentSlices.length) {
                currentSlices[i].start.play(start);
                currentSlices[i].end.play(end);
                currentSlices[i].color =
                    data.colors?.[i] ?? currentParams.colors[i];
                currentSlices[i].opacity.play(1);
            } else {
                currentSlices.push({
                    start: animator.anim(start),
                    end: animator.anim(start).play(end),
                    opacity: animator.anim(0).play(1),
                    offset: animator.anim(0),
                    color:
                        data.colors?.[i] ?? currentParams.colors[i] ?? "#000",
                });
            }

            currentAngle = end + gap;
        }

        if (currentSlices.length > data.values.length) {
            removeSlices(data.values.length);
        }

        animator.forceUpdate();
    };

    const updateData = (data: DonutUpdateData) => {
        const cx = canvas.width / 2;
        const cy = canvas.height / 2;
        const radius = calcRadius();
        const width = radius / 4;

        if (typeof data.total === "number") {
            updateDataStatic(data);
        } else {
            updateDataClassic(data);
        }

        if (currentParams.cx.toValue === 0) {
            currentParams.cx.set(cx);
            currentParams.cy.set(cy);
            currentParams.width.set(width);
        } else {
            currentParams.cx.play(cx);
            currentParams.cy.play(cy);
            currentParams.width.play(width);
        }

        currentParams.radius.set(radius);
        currentParams.values = data.values;

        if (data.colors) {
            currentParams.colors = data.colors;
        }

        if (data.labels) {
            currentParams.labels = data.labels;
        }
    };

    const getStrokeStyle = (ctx: CanvasRenderingContext2D, index: number) => {
        const slice = currentSlices[index];

        if (!gradient) {
            return slice.color;
        }

        if (gradient === true || gradient.includes(index)) {
            const rad = currentParams.radius.value + slice.offset.value;
            const gradientStroke = ctx.createLinearGradient(
                currentParams.cx.value - rad,
                currentParams.cy.value,
                currentParams.cx.value + rad,
                currentParams.cy.value,
            );
            const startColor = slice.color;
            const endColor = getDarkColor(slice.color);

            gradientStroke.addColorStop(0, startColor);
            gradientStroke.addColorStop(1, endColor);

            return gradientStroke;
        }

        return slice.color;
    };

    painter.paint((ctx) => {
        ctx.lineWidth = currentParams.width.value;

        for (let i = 0; i < currentSlices.length; ++i) {
            const slice = currentSlices[i];

            ctx.strokeStyle = getStrokeStyle(ctx, i);
            ctx.globalAlpha = slice.opacity.value;
            ctx.beginPath();
            ctx.arc(
                currentParams.cx.value,
                currentParams.cy.value,
                currentParams.radius.value + slice.offset.value,
                slice.start.value,
                slice.end.value,
            );
            ctx.stroke();
        }

        ctx.globalAlpha = 1;
    });

    if (onHover) {
        painter.listen("move");
    }

    if (onClick) {
        painter.listen("down");
    }

    const findSlice = (x: number, y: number) => {
        const cx = currentParams.cx.value;
        const cy = currentParams.cy.value;
        const radius = currentParams.radius.value;
        const width = currentParams.width.value * 1.2;
        const dx = x - cx;
        const dy = y - cy;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance >= radius - width / 2 && distance <= radius + width / 2) {
            let angle = Math.atan2(dy, dx);

            if (angle < -Math.PI / 2) {
                angle += Math.PI * 2;
            }

            return currentSlices.findIndex(
                (slice) => angle > slice.start.value && angle < slice.end.value,
            );
        }

        return -1;
    };

    const hover = (idx: number, notify: boolean, manual = false) => {
        if (idx === currentParams.hovered && !manual) {
            return;
        }

        if (idx === -1) {
            canvas.style.cursor = "default";

            if (!currentParams.leaveTmId) {
                const lastIdx = currentParams.hovered;
                const ms = manual ? 0 : 200;

                currentParams.leaveTmId = window.setTimeout(() => {
                    const last = currentSlices[lastIdx];

                    if (currentParams.clicked === -1) {
                        currentSlices.forEach((slice) => {
                            slice.opacity.play(1);
                            slice.offset.play(0);
                        });
                    } else if (last && lastIdx !== currentParams.clicked) {
                        last.opacity.play(FADED_OPACITY);
                        last.offset.play(0);
                    }

                    currentParams.leaveTmId = 0;

                    if (notify && onHover) {
                        onHover(-1);
                    }
                }, ms);
            }
        } else if (idx < currentSlices.length) {
            canvas.style.cursor = "pointer";

            if (currentParams.leaveTmId) {
                window.clearTimeout(currentParams.leaveTmId);
                currentParams.leaveTmId = 0;
            }

            currentSlices.forEach((slice, i) => {
                if (i === idx || i === currentParams.clicked) {
                    slice.opacity.play(1);
                    slice.offset.play(HOVER_OFFSET * painter.dpr);
                } else {
                    slice.opacity.play(FADED_OPACITY);
                    slice.offset.play(0);
                }
            });

            const slice = currentSlices[idx];
            const angle = (slice.start.value + slice.end.value) / 2;
            const cx = currentParams.cx.value;
            const cy = currentParams.cy.value;
            const rad =
                currentParams.radius.value + TOOLTIP_OFFSET * painter.dpr;
            const x = cx + Math.cos(angle) * rad;
            const y = cy + Math.sin(angle) * rad;

            if (notify && onHover) {
                const coord = {
                    ...painter.abs(x, y),
                    tx: Math.cos(angle) > 0 ? 0 : -1,
                    ty: Math.sin(angle) > 0 ? 0 : -1,
                };

                onHover(idx, coord);
            }
        }

        if (idx < currentSlices.length) {
            currentParams.hovered = idx;
        }
    };

    const click = (idx: number, notify: boolean) => {
        if (idx === -1) {
            return;
        }

        const reverse = idx === currentParams.clicked;

        if (reverse) {
            currentParams.clicked = -1;

            if (!notify || painter.isMobile) {
                hover(-1, false, true);
            }
        } else {
            currentParams.clicked = idx;
            hover(idx, false, true);
        }

        if (notify && onClick) {
            onClick(reverse ? -1 : idx);
        }
    };

    painter.on("move", ({ x, y }) => hover(findSlice(x, y), true));
    painter.on("down", ({ x, y }) => click(findSlice(x, y), true));

    canvas.addEventListener("mouseleave", () => {
        if (currentParams.hovered !== -1) {
            if (currentParams.clicked === -1) {
                currentSlices.forEach((slice) => {
                    slice.opacity.play(1);
                    slice.offset.play(0);
                });
            } else if (currentParams.hovered !== currentParams.clicked) {
                const last = currentSlices[currentParams.hovered];

                if (last) {
                    last.opacity.play(FADED_OPACITY);
                    last.offset.play(0);
                }
            }
            window.clearTimeout(currentParams.leaveTmId);
            onHover?.(-1);
            currentParams.hovered = -1;
        }
    });

    onResize(() => {
        const radius = calcRadius();
        const width = radius / 4;

        currentParams.radius.set(radius);
        currentParams.cx.set(canvas.width / 2);
        currentParams.cy.set(canvas.height / 2);
        currentParams.width.set(width);
    });

    updateData(initialData);

    return {
        update: updateData,
        hover: (index: number) => hover(index, false),
        click: (index: number) =>
            click(index === -1 ? currentParams.clicked : index, false),
        destroy: painter.destroy,
    };
};
